import type { OpeningPeriod } from '@bilmat/shared';
import { formatClock } from '../lib/format';
import { openingStatusAt, formatNextOpen, formatEatWindow } from '../lib/openingHours';

type Props = {
  periods: OpeningPeriod[];
  /** Googles "öppet just nu"-flagga — fallback när strukturerade tider saknas. */
  openNow: boolean | null;
  timeToReachMinutes: number;
};

const SOON_THRESHOLD_MIN = 30;

function minutesLabel(m: number): string {
  return `${m} ${m === 1 ? 'minut' : 'minuter'}`;
}

/**
 * Visar om stället är öppet när du kommer fram (nu + restid) och hur länge du
 * hinner äta. Röd varning när det är ≤ 30 min till stängning. Saknas
 * strukturerade tider faller vi tillbaka på Googles "öppet nu"-flagga;
 * saknas även den renderas inget.
 */
export function OpeningStatus({ periods, openNow, timeToReachMinutes }: Props) {
  const arrival = new Date(Date.now() + timeToReachMinutes * 60_000);
  const status = openingStatusAt(periods, arrival);

  if (status.kind === 'unknown') {
    if (openNow == null) return null;
    return (
      <div className="eat-status">
        {openNow ? (
          <span className="eat-status-main eat-status-open">🟢 Öppet nu</span>
        ) : (
          <span className="eat-status-main eat-status-closed">🔴 Stängt just nu</span>
        )}
      </div>
    );
  }

  if (status.kind === 'always-open') {
    return (
      <div className="eat-status">
        <span className="eat-status-main eat-status-open">🟢 Öppet dygnet runt</span>
      </div>
    );
  }

  if (status.kind === 'closed') {
    return (
      <div className="eat-status">
        <span className="eat-status-main eat-status-closed">🔴 Stängt när du är framme</span>
        {status.nextOpenAt && (
          <span className="eat-status-sub">{formatNextOpen(arrival, status.nextOpenAt)}</span>
        )}
      </div>
    );
  }

  const closeClock = formatClock(status.closeAt);
  const soon = status.minutesToClose <= SOON_THRESHOLD_MIN;

  return (
    <div className="eat-status">
      <span className="eat-status-main eat-status-open">
        🟢 Öppet när du är framme{soon ? ` · stänger ${closeClock}` : ''}
      </span>
      {soon ? (
        <span className="eat-status-warn">
          ⚠ Du har {minutesLabel(status.minutesToClose)} på dig att äta
        </span>
      ) : (
        <span className="eat-status-sub">
          Stänger {closeClock} · {formatEatWindow(status.minutesToClose)} att äta
        </span>
      )}
    </div>
  );
}
